import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Polygon, Rectangle, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { getUser } from "../../utilities/adminservice";
import { getLieuxPage } from "../../utilities/locationService";
import { modeleTuilesLeaflet, sousDomainesLeaflet, attributionFond } from "../../utilities/basemap";
import locationApi from "../../utilities/locationApi";
import { invaliderAttente, lireDate, useAttente } from "../../navigation/attente";
import { Data, toDataRow, useFicheLieu } from "../lieux/logiqueLieux";
import { FicheLieuModal } from "../lieux/composantsLieux";
import AFaire from "./AFaire";
import TitreSection from "../../components/charte/TitreSection";
import Bloc from "../../components/charte/Bloc";
import { BoutonPrincipal } from "../../components/charte/Boutons";
import { COULEURS, COULEUR_DOMAINE } from "../../theme/tribeTheme";
import { AIDE_ACCUEIL } from "../../aide/textesAide";

/* ══════════════════════════════════════════════════════════════════════════
   Accueil. Données lues uniquement par des appels existants :
   - prénom : GET /admin (getUser, profil de l'admin connecté) ;
   - À faire maintenant, quêtes, réponses : navigation/attente.ts ;
   - carte : lieux à valider (getLieuxPage, onglets attente et verifie, 100
     premiers de chaque) et zones des quêtes publiées (toutesLesQuetes) ;
   - Cette semaine : GET /location/positions (appel du tableau des collectes)
     et soumissions des quêtes déjà lues pour les réponses.
   « Cette semaine » = les 7 derniers jours glissants.
   ══════════════════════════════════════════════════════════════════════════ */

const SEMAINE_MS = 7 * 86400000;
const DANS_LA_SEMAINE = (d: Date | null) => !!d && Date.now() - d.getTime() <= SEMAINE_MS;
const BORNES_SENEGAL: [[number, number], [number, number]] = [[12.3, -17.6], [16.7, -11.3]];

function polygoneDe(q: any): [number, number][] {
  return (Array.isArray(q.zonePolygon) ? q.zonePolygon : [])
    .filter((p: any) => Array.isArray(p) && p.length >= 2 && isFinite(p[0]) && isFinite(p[1]))
    .map((p: any) => [Number(p[0]), Number(p[1])] as [number, number]);
}
const aUnRectangle = (q: any) => [q.latMin, q.latMax, q.lngMin, q.lngMax].every(v => v != null && isFinite(v));

function Avancement({ faites, attendues }: { faites: number; attendues: number | null }) {
  const part = attendues && attendues > 0 ? Math.min(1, faites / attendues) : null;
  return (
    <Box sx={{ minWidth: 160 }}>
      <Box sx={{ height: 8, background: COULEURS.separateur, borderRadius: "2px", overflow: "hidden" }}
        role="progressbar" aria-valuemin={0} aria-valuemax={attendues ?? undefined} aria-valuenow={faites}>
        {part != null && <Box sx={{ height: "100%", width: `${part * 100}%`, background: COULEURS.texte }} />}
      </Box>
      <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)", mt: 0.5 }}>
        {attendues ? `${faites.toLocaleString("fr-FR")} / ${attendues.toLocaleString("fr-FR")} collectes` : `${faites.toLocaleString("fr-FR")} collecte${faites > 1 ? "s" : ""} (objectif non fixé)`}
      </Typography>
    </Box>
  );
}

function Chiffre({ valeur, libelle, aide }: { valeur: number | null; libelle: string; aide: string }) {
  return (
    <Bloc sx={{ p: 2, flex: 1, minWidth: 180 }}>
      <Typography sx={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 30, lineHeight: 1.1 }}>
        {valeur == null ? "—" : valeur.toLocaleString("fr-FR")}
      </Typography>
      <Typography sx={{ fontWeight: 600, fontSize: 14, mt: 0.5 }}>{libelle}</Typography>
      <Typography sx={{ fontSize: 12.5, color: "var(--c-text-secondary)" }}>{aide}</Typography>
    </Bloc>
  );
}

export default function Accueil() {
  const navigate = useNavigate();
  const attente = useAttente(true);
  const [prenom, setPrenom] = useState<string | null>(null);
  const [lieux, setLieux] = useState<Data[]>([]);
  const [positions, setPositions] = useState<any[] | null>(null);

  useEffect(() => {
    getUser().then((u: any) => setPrenom((u?.first_name ?? "").trim() || null)).catch(() => setPrenom(null));
    locationApi.get("/location/positions")
      .then(r => setPositions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPositions(null));
  }, []);

  const chargerLieux = () => {
    Promise.allSettled([
      getLieuxPage({ onglet: "attente", page: 0, taille: 100 }),
      getLieuxPage({ onglet: "verifie", page: 0, taille: 100 }),
    ]).then(res => {
      const tous = res.flatMap(r => r.status === "fulfilled" ? (r.value.contenu ?? []) : []);
      setLieux(tous.map(toDataRow).filter(l => l.latitude != null && l.longitude != null));
    });
  };
  useEffect(chargerLieux, []);

  // Décision prise depuis la carte : points et compteurs relus.
  const fiche = useFicheLieu(() => { chargerLieux(); invaliderAttente(); });

  const enCours = useMemo(() => attente.quetes
    .filter(q => (q.statut ?? "").toLowerCase() === "publiee")
    .sort((a, b) => (lireDate(a.dateFin)?.getTime() ?? Infinity) - (lireDate(b.dateFin)?.getTime() ?? Infinity)), [attente.quetes]);

  // Cette semaine (7 jours glissants).
  const semaine = useMemo(() => {
    const lieuxSemaine = positions
      ? positions.filter(p => p.createdBy !== "osm_import" && DANS_LA_SEMAINE(lireDate(p.createdOn)))
      : null;
    const reponsesSemaine = attente.charge ? attente.soumissions.filter(s => DANS_LA_SEMAINE(lireDate(s.createdAt))) : null;
    const spotters = lieuxSemaine && reponsesSemaine
      ? new Set([...lieuxSemaine.map(p => p.createdBy), ...reponsesSemaine.map(s => s.collecteurEmail)].filter(Boolean)).size
      : null;
    return { lieux: lieuxSemaine?.length ?? null, reponses: reponsesSemaine?.length ?? null, spotters };
  }, [positions, attente.soumissions, attente.charge]);

  const bornes = useMemo(() => {
    const pts: [number, number][] = [
      ...lieux.map(l => [l.latitude!, l.longitude!] as [number, number]),
      ...enCours.flatMap(q => aUnRectangle(q) ? [[q.latMin, q.lngMin], [q.latMax, q.lngMax]] as [number, number][] : polygoneDe(q)),
    ];
    if (pts.length < 2) return BORNES_SENEGAL;
    const lats = pts.map(p => p[0]), lngs = pts.map(p => p[1]);
    return [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]] as [[number, number], [number, number]];
  }, [lieux, enCours]);

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, maxWidth: 1320 }}>
      {/* Salutation et action principale */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3.5, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography component="h1" sx={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: { xs: 26, md: 30 }, lineHeight: 1.15 }}>
            Bonjour{prenom ? ` ${prenom}` : ""}
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 15, color: "var(--c-text-secondary)" }}>
            Voici ce qui attend une décision, ce qui se passe sur le terrain et le bilan des 7 derniers jours.
          </Typography>
        </Box>
        <BoutonPrincipal onClick={() => navigate("/quetes/creer")}>Créer une quête</BoutonPrincipal>
      </Box>

      {/* À faire maintenant */}
      <Box component="section" sx={{ mb: 4 }}>
        <TitreSection titre="À faire maintenant"
          aide={AIDE_ACCUEIL.aFaire} />
        <AFaire attente={attente} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 3, mb: 4 }}>
        {/* Sur le terrain */}
        <Box component="section">
          <TitreSection titre="Sur le terrain"
            aide={AIDE_ACCUEIL.surLeTerrain} />
          <Box sx={{ border: "1.5px solid var(--c-text-primary)", borderRadius: "8px", overflow: "hidden" }}>
            <MapContainer key={JSON.stringify(bornes)} bounds={bornes} boundsOptions={{ padding: [24, 24], maxZoom: 15 }}
              style={{ height: 360, width: "100%" }} scrollWheelZoom={false}>
              <TileLayer url={modeleTuilesLeaflet()} attribution={attributionFond}
                {...({ subdomains: sousDomainesLeaflet() } as any)} />
              {enCours.map(q => {
                const style = { color: COULEUR_DOMAINE.quetes, weight: 2, fillOpacity: 0.08 };
                const info = <Tooltip sticky><b>{q.titre ?? "Quête"}</b><br />Quête en cours</Tooltip>;
                if (aUnRectangle(q)) {
                  return <Rectangle key={q.id} bounds={[[q.latMin, q.lngMin], [q.latMax, q.lngMax]]} pathOptions={style}
                    eventHandlers={{ click: () => navigate(`/quetes/${q.id}`) }}>{info}</Rectangle>;
                }
                const poly = polygoneDe(q);
                return poly.length >= 3
                  ? <Polygon key={q.id} positions={poly} pathOptions={style} eventHandlers={{ click: () => navigate(`/quetes/${q.id}`) }}>{info}</Polygon>
                  : null;
              })}
              {lieux.map(l => (
                <CircleMarker key={l.referenceId} center={[l.latitude!, l.longitude!]} radius={7}
                  pathOptions={{ fillColor: "#7C5600", color: COULEURS.texte, weight: 1.5, fillOpacity: 0.9 }}
                  eventHandlers={{ click: () => fiche.ouvrirFiche(l) }}>
                  <Tooltip direction="top" offset={[0, -6]}>
                    <b>{l.Location_Name || "Lieu sans nom"}</b><br />{l.Category} · à valider
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </Box>
          <Box sx={{ display: "flex", gap: 2.5, mt: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 14, height: 10, border: `2px solid ${COULEUR_DOMAINE.quetes}` }} />
              <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>Zone d'une quête en cours</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "#7C5600", border: `1px solid ${COULEURS.texte}` }} />
              <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>Lieu à valider ({lieux.length})</Typography>
            </Box>
          </Box>
        </Box>

        {/* Quêtes en cours */}
        <Box component="section">
          <TitreSection titre="Quêtes en cours" sousTitre={enCours.length ? `${enCours.length}` : undefined}
            aide={AIDE_ACCUEIL.quetesEnCours} />
          <Bloc sx={{ overflow: "hidden" }}>
            {enCours.length === 0 && (
              <Typography sx={{ p: 2, fontSize: 14, color: "var(--c-text-secondary)" }}>
                {attente.charge ? "Aucune quête publiée en ce moment." : "Lecture des quêtes…"}
              </Typography>
            )}
            {enCours.slice(0, 8).map((q, i) => {
              const fin = lireDate(q.dateFin);
              return (
                <Box key={q.id} component={RouterLink} to={`/quetes/${q.id}`} sx={{
                  display: "flex", alignItems: "center", gap: 2, px: 2, py: 1.5, textDecoration: "none", color: "inherit",
                  borderTop: i ? "1px solid var(--c-border)" : "none", "&:hover": { background: "var(--c-surface-2)" },
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.titre || "Quête sans titre"}</Typography>
                    <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>
                      {fin ? `jusqu'au ${fin.toLocaleDateString("fr-FR")}` : "sans date de fin"}{q.source === "client_ia" ? " · demande client" : ""}
                    </Typography>
                  </Box>
                  <Avancement faites={Number(q.collectesRealisees) || 0} attendues={Number(q.collectesAttendues) || null} />
                </Box>
              );
            })}
            {enCours.length > 8 && (
              <Box sx={{ px: 2, py: 1.25, borderTop: "1px solid var(--c-border)" }}>
                <RouterLink to="/quetes/liste" style={{ color: "inherit", fontWeight: 600, fontSize: 13 }}>Toutes les quêtes ({enCours.length} en cours)</RouterLink>
              </Box>
            )}
          </Bloc>
        </Box>
      </Box>

      {/* Cette semaine */}
      <Box component="section">
        <TitreSection titre="Cette semaine" sousTitre="7 derniers jours"
          aide={AIDE_ACCUEIL.cetteSemaine} />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Chiffre valeur={semaine.lieux} libelle="Lieux envoyés" aide="Nouveaux lieux collectés par les Spotters." />
          <Chiffre valeur={semaine.reponses} libelle="Réponses aux quêtes" aide="Collectes reçues sur les quêtes, tous statuts." />
          <Chiffre valeur={semaine.spotters} libelle="Spotters actifs" aide="Spotters ayant envoyé au moins un lieu ou une réponse." />
        </Box>
      </Box>

      <FicheLieuModal fiche={fiche} onRelance={() => { chargerLieux(); invaliderAttente(); }} />
    </Box>
  );
}
